import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { authMe, authLogin } from "@/lib/blogApi";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    authMe()
      .then((u) => {
        if (u && u.is_admin) navigate("/admin", { replace: true });
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const user = await authLogin(email.trim(), password);
      if (!user.is_admin) {
        setError("This account does not have admin access.");
        return;
      }
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err) || "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="login-page">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto rounded-full border-4 border-gray-200 border-t-[#D41F30] animate-spin mb-3" />
          <p className="text-sm text-gray-500">Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 py-12" data-testid="login-page">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 max-w-md w-full p-8 md:p-10">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D41F30] flex items-center justify-center mb-5">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-2 leading-tight">
            CityWorth Admin
          </h1>
          <p className="text-gray-600 mb-8">Sign in to manage the blog and content.</p>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <label className="block">
            <span className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-1.5 block">
              Email
            </span>
            <div className="relative">
              <Mail className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@cityworth.com"
                data-testid="login-email"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 focus:border-[#D41F30]"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-1.5 block">
              Password
            </span>
            <div className="relative">
              <Lock className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                data-testid="login-password"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 focus:border-[#D41F30]"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] disabled:opacity-60 disabled:cursor-not-allowed text-white border-2 border-[#D41F30] rounded-full px-6 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        <p className="mt-6 text-xs text-gray-400 text-center">
          Locked out? Wait 15 minutes after 5 failed attempts.
        </p>
      </div>
    </div>
  );
}

function formatApiError(err) {
  const detail = err?.data?.detail;
  if (detail == null) return err?.message || "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}
